<template>
  <td v-if="renderCell" class="fecha-template" :class="[{ editable: isEditable }, className]" @click="$emit('cellclick', { dataItem, field, event: $event })">
    <template v-if="isEditable">
      <el-time-picker v-if="isTimeFormat" ref="cellinput" class="!w-full" v-model="localValue" :format="dateTimeFormat" :value-format="dateValueFormat" popper-class="fecha-template-picker" size="mini" tabindex="0" arrow-control @change="$emit('change', null, $event)" />
      <el-date-picker v-else ref="cellinput" class="!w-full" v-model="localValue" :type="formatType" :format="dateTimeFormat" :value-format="dateValueFormat" popper-class="fecha-template-picker" size="mini" tabindex="0" @change="$emit('change', null, $event)" />
    </template>
    <template v-else>
      {{ value }}
    </template>
  </td>
  <td v-else-if="renderArrow" :key="`g-${columnIndex}`" class="k-group-cell" />
  <td v-else-if="renderGroup" key="g-colspan" class="fecha-template" :colSpan="columnsCount - columnIndex">
    <p class="k-reset">
      <a @click="$emit('click', $event, dataItem, expanded)" tabIndex="-1" href="#" :class="expanded ? 'k-i-collapse k-icon' : 'k-i-expand k-icon'" />
      {{ groupHeader }}
    </p>
  </td>
</template>
<script>
import moment from 'moment'
import { i18n } from '@/i18n'
import getNestedValue from '@/services/functions/getNestedValue'
import getLocaleDateTimeFormat from '@/services/functions/getLocaleDateTimeFormat'
import { discard } from '@/services/functions/preserve&discard'
import NULL from '@/constants/NULL'

export default {
  name: 'fecha-template',
  props: {
    tableId: [String, Number],
    field: String,
    dataItem: Object,
    format: String,
    className: String,
    columnIndex: Number,
    columnsCount: Number,
    rowType: String,
    level: Number,
    expanded: Boolean,
    editor: String,
    type: {
      type: String,
      default: 'Text'
    },
    curEditField: [String, null],
    editType: {
      type: [String, Boolean],
      default: 'row'
    },
    editField: {
      type: String,
      default: 'inEdit'
    },
    extraparams: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      localValue: '',
    }
  },
  mounted () {
    this.localValue = this.dateValue
    if (this.isEditable && this.curEditField === this.field && this.$refs.cellinput) {
      this.$refs.cellinput.focus()
    }
  },
  watch: {
    dateValue: function (value, oldValue) {
      if (value !== oldValue) {
        this.localValue = value
      }
    },
    isEditable: function (value, oldValue) {
      if (value && value !== oldValue) {
        this.$nextTick(() => {
          if (this.isEditable && this.curEditField === this.field && this.$refs.cellinput) {
            this.$refs.cellinput.focus()
          }
        })
      }
    }
  },
  computed: {
    renderArrow () {
      var returnValue =
        this.columnIndex === undefined ||
        this.level === undefined ||
        this.columnIndex < this.level ||
        this.columnsCount === undefined ||
        this.rowType !== 'groupHeader' ||
        this.dataItem[this.field] === undefined
      // return returnValue && this.dataItem[this.field]
      return returnValue && this.dataItem[this.field] !== undefined
    },
    renderCell () {
      return this.field !== undefined && this.rowType !== 'groupHeader'
    },
    renderGroup () {
      return this.columnIndex <= this.level
    },
    inEdit: function () {
      return getNestedValue(this.editField, this.dataItem) === true
    },
    value: function () {
      const value = getNestedValue(this.field, this.dataItem)
      return this.formatDate(value === NULL ? null : value)
    },
    groupHeader () {
      const value = this.value
      if (this.rowType === 'groupHeader') {
        const template = this.extraparams.groupHeaderTemplate
        if (typeof template === 'function') {
          return template(this.dataItem, { value, ...discard([template.name], this.extraparams) })
        }
      }
      return value
    },
    separators () {
      return this.extraparams?.separators ?? {}
    },
    dateTimeFormat: function () {
      const formatKey = this.format
      const locale = i18n.locale || 'es'
      if (i18n.de(formatKey)) {
        return getLocaleDateTimeFormat(formatKey, locale, this.separators)
      }
      return this.format
    },
    formatType: function () {
      const format = this.dateTimeFormat
      const hasTime = format.includes('H') || format.includes('h') || format.includes('m') || format.includes('s')
      const hasDate = format.includes('y') || format.includes('d') || format.includes('M')
      if (hasTime) {
        if (hasDate) {
          return 'datetime'
        }
        return 'time'
      }
      return 'date'
    },
    isDateTimeFormat: function () {
      return this.formatType === 'datetime'
    },
    isTimeFormat: function () {
      return this.formatType === 'time'
    },
    isDateFormat: function () {
      return this.formatType === 'date'
    },
    dateValueFormat: function () {
      const timeFields = ['HH', 'mm', 'ss']
      const tokens = this.dateTimeFormat.split(/[^a-zA-Z]+/)
      const curTime = tokens.filter(token => timeFields.includes(token))
      const finalTime = timeFields.map(unidad => curTime.includes(unidad) ? unidad : '[00]').join(':')
      return (this.isTimeFormat ? `[1970-01-01T]${finalTime}` : `yyyy-MM-dd[T]${finalTime}`).replaceAll(/\](:?)\[/g, '$1')
    },
    dateValue: function () {
      const strDate = getNestedValue(this.field, this.dataItem)
      if (!this.isValidDate(strDate)) return strDate
      return moment(strDate).locale(i18n.locale).format(this.dateValueFormat.replace('dd', 'DD'))
    },
    editable: function () {
      return !!this.extraparams.editable
    },
    isCellEditable: function () {
      return this.editType === 'cell'
    },
    isEditable: function () {
      return this.editable && this.inEdit && (!this.isCellEditable || this.curEditField === this.field)
    }
  },
  methods: {
    formatDate: function (strDate, format = this.format, separators = this.separators) {
      if (!this.isValidDate(strDate)) return strDate
      if (i18n.de(format)) return i18n.d(strDate, format, separators)
      return moment(strDate).locale(i18n.locale).format(format) // .format('DD-MM-yyyy HH:mm')
    },
    isValidDate: function (strDate) {
      if (strDate === undefined || strDate === null) return false
      return !Number.isNaN(new Date(strDate).getTime())
    }
  }
}
</script>

<style scoped>
.bg-success-p{
  background-color: black;
}
.k-grid td.editable {
  text-overflow: clip;
}
.k-grid td.editable::v-deep .el-input__inner {
  min-width: calc(30px + 1em);
}
</style>
